import api from '@/core/config/axios'
import {
  createCatalogEndpoint,
  createCrudEndpoints,
  createNestedCrudEndpoints,
} from '@/core/utils'
import { MaybeRef, toValue } from 'vue'
import {
  TCatalogs,
  TCustomer,
  TCustomers,
  TLot,
  TLots,
  TPaginatedCatalogs,
  TProjects,
  TSale,
  TSaleFile,
  TSaleFileAction,
  TSaleFiles,
  TSales,
} from '.'
import { TLeads } from '../leads'

type ServiceParams = {
  modelId?: MaybeRef<number | string>
  categoryId?: MaybeRef<number | string>
}

export const serviceConfig = {
  model: 'sales',
  version: 'v1',
  projects: {
    model: 'projects',
    version: 'v1',
  },
  lots: {
    model: 'lots',
    version: 'v1',
  },
  customers: {
    model: 'customers',
    version: 'v1',
  },
  leads: {
    model: 'leads',
    version: 'v1',
  },
  paymentMethods: {
    model: 'payment-methods',
    version: 'v1',
  },
  paymentForms: {
    model: 'payment-forms',
    version: 'v1',
  },
  cfdis: {
    model: 'cfdis',
    version: 'v1',
  },
  classifications: {
    model: 'classifications',
    version: 'v1',
  },
  fiscalRegimes: {
    model: 'fiscal-regimes',
    version: 'v1',
  },
  currencies: {
    model: 'currencies',
    version: 'v1',
  },
  countries: {
    model: 'countries',
    version: 'v1',
  },
  states: {
    model: 'states',
    version: 'v1',
  },
  postalCodes: {
    model: 'postal-codes',
    version: 'v1',
  },
  files: {
    model: 'files',
    version: 'v1',
  },
  saleFiles: {
    model: 'sale-files',
    version: 'v1',
  },
}

const {
  projects,
  lots,
  customers,
  leads,
  cfdis,
  classifications,
  countries,
  currencies,
  fiscalRegimes,
  paymentForms,
  paymentMethods,
  postalCodes,
  states,
  files,
  saleFiles,
} = serviceConfig

const moduleServiceHandlers = createCrudEndpoints<TSales, TSale>(
  serviceConfig.model,
  serviceConfig.version,
)

const projectsCatalogService = createCatalogEndpoint<TProjects>(
  projects.model,
  projects.version,
)

const lotsServiceHandler = createCrudEndpoints<TLots, TLot>(
  lots.model,
  lots.version,
)

const customersServiceHandlers = createCrudEndpoints<TCustomers, TCustomer>(
  customers.model,
  customers.version,
)

const customerSalesHandlers = createNestedCrudEndpoints<TCustomers, TCustomer>(
  serviceConfig.model,
  customers.model,
  customers.version,
)

const leadServiceHandlers = createCatalogEndpoint<TLeads>(
  leads.model,
  leads.version,
)

const paymentMethodsServiceHandlers = createCatalogEndpoint<TCatalogs>(
  paymentMethods.model,
  paymentMethods.version,
)

const paymentFormsServiceHandlers = createCatalogEndpoint<TCatalogs>(
  paymentForms.model,
  paymentForms.version,
)

const cfdisServiceHandlers = createCatalogEndpoint<TPaginatedCatalogs>(
  cfdis.model,
  cfdis.version,
)

const classificationsServiceHandlers = createCatalogEndpoint<TCatalogs>(
  classifications.model,
  classifications.version,
)

const fiscalRegimesServiceHandlers = createCatalogEndpoint<TCatalogs>(
  fiscalRegimes.model,
  fiscalRegimes.version,
)

const currenciesServiceHandlers = createCatalogEndpoint<TCatalogs>(
  currencies.model,
  currencies.version,
)

const countriesServiceHandlers = createCatalogEndpoint<TCatalogs>(
  countries.model,
  countries.version,
)

const statesServiceHandlers = createCatalogEndpoint<TPaginatedCatalogs>(
  states.model,
  states.version,
)

const postalCodesServiceHandlers = createCatalogEndpoint<TPaginatedCatalogs>(
  postalCodes.model,
  postalCodes.version,
)

const salesFilesHandlers = createNestedCrudEndpoints<TSaleFiles, TSaleFile>(
  serviceConfig.model,
  files.model,
  files.version,
)

const filesServiceHandlers = {
  onAction: async (id: number, payload: TSaleFileAction): Promise<TSale> =>
    api
      .put(`v1/${saleFiles.model}/${toValue(id)}`, payload)
      .then((response) => response.data),
}

const uploadServiceHandlers = {
  loadFilesByCustomer: async (params: ServiceParams): Promise<TCustomer[]> =>
    api
      .get(
        `${serviceConfig.version}/${serviceConfig.model}/${toValue(
          params.modelId,
        )}/files-to-upload`,
      )
      .then((response) => response.data),

  uploadDocument: async (
    params: ServiceParams,
    payload: FormData,
  ): Promise<void> =>
    api
      .post(`v1/customers/${toValue(params.modelId)}/files/`, payload)
      .then((response) => response.data),
}

const uploadSaleFilesServiceHandlers = {
  uploadDocument: async (
    params: ServiceParams,
    payload: FormData,
  ): Promise<void> =>
    api
      .post(`v1/sale-files/${toValue(params.modelId)}`, payload)
      .then((response) => response.data),
}

const searchCustomerServiceHandlers = {
  searchCustomer: async (rfc: string): Promise<TCustomer> =>
    api
      .get(
        `${serviceConfig.version}/${serviceConfig.customers.model}/${rfc}/rfc`,
      )
      .then((response) => response.data),
}

export {
  cfdisServiceHandlers,
  classificationsServiceHandlers,
  countriesServiceHandlers,
  currenciesServiceHandlers,
  customerSalesHandlers,
  customersServiceHandlers,
  filesServiceHandlers,
  fiscalRegimesServiceHandlers,
  leadServiceHandlers,
  lotsServiceHandler,
  moduleServiceHandlers,
  paymentFormsServiceHandlers,
  paymentMethodsServiceHandlers,
  postalCodesServiceHandlers,
  projectsCatalogService,
  salesFilesHandlers,
  searchCustomerServiceHandlers,
  statesServiceHandlers,
  uploadSaleFilesServiceHandlers,
  uploadServiceHandlers,
}
